<template>
    <div class="attr">
        <span class="attr-label">{{ value.label }}</span>
        <div class="attr-cat-badges">
            <span class="attr-cat-badge">
                <i :class="categoryIcon"></i>
                {{ categoryLabel }}
            </span>
            <template v-if="subCategoryLabel">
                <i class="fal fa-chevron-right attr-cat-separator"></i>
                <span class="attr-cat-badge attr-cat-badge--sub">
                    <i :class="subCategoryIcon"></i>
                    {{ subCategoryLabel }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
export default {
    name: 'CategoryAttribute',
    props: ['value'],
    computed: {
        answer() {
            const raw = this.value.value
            // Un vieux ticket a pu stocker le seul libellé plutôt que l'objet.
            return typeof raw === 'object' && raw !== null ? raw : { label: raw }
        },
        categoryLabel() {
            return this.answer.label || ''
        },
        subCategoryLabel() {
            // `sub_category` est la clé de l'API ; `subcategory` a été écrite un
            // temps par le wizard et survit dans les tickets existants.
            return this.answer.sub_category || this.answer.subcategory || null
        },
        // La réponse ne stocke que des libellés : les icônes se relisent dans la
        // config de l'attribut, comme le fait le mur des contributions côté API.
        // Le libellé est figé dans la langue de saisie, donc on balaie toutes les
        // locales et pas seulement celle affichée.
        optionsByLocale() {
            let config = this.value.config
            if (typeof config === 'string') {
                try {
                    config = JSON.parse(config)
                } catch (e) {
                    return []
                }
            }
            const options = (config && config.options) || null
            if (!options) return []
            const locales = [this.$i18n.locale, 'fr', ...Object.keys(options)]
            return locales
                .filter((locale, index) => locales.indexOf(locale) === index)
                .map((locale) => options[locale])
                .filter((list) => Array.isArray(list))
        },
        selectedOption() {
            if (!this.categoryLabel) return null
            for (const list of this.optionsByLocale) {
                const found = list.find((option) => option.label === this.categoryLabel)
                if (found) return found
            }
            return null
        },
        selectedChild() {
            if (!this.selectedOption || !this.subCategoryLabel) return null
            return (this.selectedOption.children || [])
                .find((child) => child.label === this.subCategoryLabel) || null
        },
        categoryIcon() {
            return (this.answer.icon)
                || (this.selectedOption && this.selectedOption.icon)
                || 'fal fa-tag'
        },
        subCategoryIcon() {
            return (this.selectedChild && this.selectedChild.icon) || 'fal fa-tag'
        }
    }
}
</script>

<style scoped>
.attr-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin-bottom: 6px;
}

.attr-cat-badges {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.attr-cat-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    background: #eff6ff;
    color: #2563eb;
    border-radius: 20px;
    font-size: 0.82rem;
    font-weight: 600;
}

.attr-cat-badge i {
    font-size: 0.75rem;
}

.attr-cat-badge--sub {
    background: #f0fdfa;
    color: #0891B2;
}

.attr-cat-separator {
    font-size: 0.65rem;
    color: #cbd5e1;
}
</style>
